import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, filter, map } from 'rxjs';
import { LogService } from '../baseServices/logging/log.service';
import { GlobalConstants } from '../constant/constant';
import { User } from '../entities/User';
import { AppEventType } from '../enums/app-events.enum';
import { EventBusService } from './event-bus.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AppUserService implements OnDestroy {
  //#region State Management

  private readonly currentUserIdSubject = new BehaviorSubject<number | null>(null);
  /** Emits the current user id (null if logged out). */
  readonly currentUserId$ = this.currentUserIdSubject.asObservable();
  /** Gets the current user id (null if logged out). */
  get currentUserId(): number | null { return this.currentUserIdSubject.value; }

  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  /** Emits the current user entity (null if logged out). */
  readonly currentUser$ = this.currentUserSubject.asObservable();
  /** Gets the current user entity (null if logged out). */
  get currentUser(): User | null { return this.currentUserSubject.value; }

  //#endregion
  //#region Properties

  private subscriptions: Subscription[] = [];

  //#endregion
  //#region Lifecycle

  constructor(
    private userService: UserService,
    private eventBusService: EventBusService
    /**
     * @todo Implement facade to resolve circular dependency:
     * LogService -> ApplicationInsightsService -> AppUserService -> LogService.
     * For now, using console.* in this service. When facade is added, re-introduce:
     * private logService: LogService
     */
  ) {
    this.loadSubscriptions();
    this.loadState();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  //#endregion
  //#region Initialization

  private async loadState(): Promise<void> {
    try {
      const currentUserId = this.getCurrentUserIdFromStorage();
      this.setCurrentUserId(currentUserId);
      if (currentUserId != null) {
        const currentUser = await this.getCurrentUserById(currentUserId);
        this.setCurrentUser(currentUser);
      } else {
        this.setCurrentUser(null);
      }
      console.debug('AppUser.Init', { currentUserId: this.currentUserId });
    } catch (err) {
      console.error('AppUser.InitFailed', err);
      this.clearCurrentUser();
    }
  }

  private loadSubscriptions(): void {
    const loginSuccessSubscription = this.eventBusService.on(AppEventType.LOGIN_SUCCESS, () => {
      this.loadState();
    });
    this.subscriptions.push(loginSuccessSubscription);

    const logoutSuccessSubscription = this.eventBusService.on(AppEventType.LOGOUT_SUCCESS, () => {
      this.clearCurrentUser();
    });
    this.subscriptions.push(logoutSuccessSubscription);

    const userUpdatedSubscription = this.userService.changeEmitted$.pipe(
      map(e => e.data),
      filter(u => u.Id === this.currentUserId)
    ).subscribe(user => {
      this.setCurrentUser(user);
      console.info('AppUser.UserUpdated', { user });
    });
    this.subscriptions.push(userUpdatedSubscription);
  }

  //#endregion
  //#region Private Methods

  private getCurrentUserIdFromStorage(): number | null {
    try {
      const raw = localStorage.getItem(GlobalConstants.USER_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed?.Id === 'number' ? parsed.Id : null;
    } catch (err) {
      console.error('AppUser.GetCurrentUserIdFromStorage', err);
      return null;
    }
  }

  private async getCurrentUserById(id: number): Promise<User | null> {
    try {
      return await this.userService.getUserByOnlineId(id);
    } catch (err) {
      console.warn('AppUser.GetUserFailed', { id, err });
      return null;
    }
  }

  private setCurrentUserId(id: number | null): void {
    if (this.currentUserId !== id) {
      this.currentUserIdSubject.next(id);
    }
  }

  private setCurrentUser(user: User | null): void {
    this.currentUserSubject.next(user);
  }

  private clearCurrentUser(): void {
    this.setCurrentUser(null);
    this.setCurrentUserId(null);
  }


}